import Button from "components/Button";
import Modal from "components/Modal";
import { format } from "date-fns";
import html2canvas from "html2canvas";
import React, { useCallback, useMemo, useRef, useState } from "react";
import JsxParser from "react-jsx-parser";
import constants from "utils/constants";
import QuotationResultContact from "./QuotationResultContact";
import QuotationResultGrid from "./QuotationResultGrid";
import QuotationResultList from "./QuotationResultList";
import QuotationResultText from "./QuotationResultText";
import styles from "./styles.module.scss";

interface Props {
  data: {
    address: string;
    electricBill: string;
    email: string;
    name: string;
    phone: string;
    range: string;
    size: number;
  };
  onBack?: () => void;
  onClose?: () => void;
}

const QuotationResult: React.FC<Props> = ({ data, onBack, onClose }) => {
  const ref = useRef<HTMLDivElement>(null);

  const [isDownloading, setIsDownloading] = useState(false);

  const handleSave = useCallback(async () => {
    if (!ref.current || isDownloading) {
      return;
    }
    setIsDownloading(true);

    try {
      const canvas = await html2canvas(ref.current, {
        useCORS: true,
        scale: 1.25,
      });
      const dataUrl = canvas.toDataURL("image/png");

      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = "quotation.png";
      link.click();
    } catch (error) {
      console.log(error);
    } finally {
      setIsDownloading(false);
    }
  }, [isDownloading]);

  const currentDate = useMemo(() => {
    return format(new Date(), "MMM dd, yyyy");
  }, []);

  const systemBreakdown = useMemo(
    () => [
      {
        title: "Item",
        description: "Description",
      },
      {
        title: "Solar Panels",
        description:
          "TIER 1 solar panels for top-tier quality and exceptional reliability, ensuring long-term performance.",
      },
      {
        title: "Mounting Structures",
        description:
          "Versatile mounting solutions for any roof type, providing a secure foundation for your solar system.",
      },
      {
        title: "Inverter",
        description:
          "	High-efficiency inverter with lifetime online monitoring for real-time energy production insights.",
      },
      {
        title: "Circuit Breakers & Surge Protection Devices",
        description:
          "Premium protection devices to safeguard your home from power surges and electrical incidents.",
      },
      {
        title: "Rapid Shutdown Devices",
        description:
          "	Advanced safety devices that reduce risks and ensure system security.",
      },
      {
        title: "AC Wires (up to 10m)",
        description:
          "Durable, high-quality wires for seamless energy distribution.",
      },
      {
        title: "DC Wires (up to 50m)",
        description:
          "Premium copper wires for optimal energy flow from your solar panels.",
      },
      {
        title: "Conduits (up to 25m)",
        description: "Strong and reliable conduits to protect system wiring.",
      },
      {
        title: "Battery (Add-on)",
        description:
          "Store excess energy for backup power during outages with high-performance batteries.",
      },
    ],
    []
  );

  const inclusions = useMemo(
    () => [
      "Free Site Visit",
      "Free Online Consultations",
      "Free Lifetime Monthly System Performance Reporting",
      "Free 2 Years of Maintenance Services",
      "30-Year Solar Panel Warranty",
      "5-Year Inverter Warranty",
      "2-Year Workmanship Warranty",
    ],
    []
  );

  return (
    <Modal
      className={styles.result}
      footer={
        <>
          <Button className={styles.footerButton} onClick={onBack} type="light">
            Back
          </Button>

          <Button
            className={styles.footerButton}
            onClick={handleSave}
            type="primary"
          >
            Download Quotation
          </Button>
        </>
      }
      onClose={onClose}
      subtitle={
        <JsxParser
          className={styles.subtitle}
          components={{
            facebook: (data: { children?: React.ReactNode }) => {
              return (
                <Button
                  className={styles.button}
                  href={constants.facebook}
                  iconClassName={styles.icon}
                  showNewWindowIcon
                  target="_blank"
                  type="link"
                >
                  {data.children}
                </Button>
              );
            },
            highlight: (data: { children?: React.ReactNode }) => {
              return (
                <span className={styles.highlighted}>{data.children}</span>
              );
            },
          }}
          jsx={`Send this quotation on our <facebook>Facebook page</facebook> to schedule your <highlight>FREE</highlight> site visit!`}
        />
      }
      title="Free Quotation"
      visible
    >
      <div className={styles.content}>
        <div className={styles.quotation} ref={ref}>
          <div className={styles.title}>Indicative Sales Proposal</div>

          <div className={styles.header}>
            <div className={styles.logoContainer}>
              <div
                className={styles.logo}
                style={{ backgroundImage: `url('/logo.png')` }}
              />
              Renewable Electrical Engineering Services
            </div>

            <div className={styles.date}>{currentDate}</div>
          </div>

          <div className={styles.details}>
            <QuotationResultContact
              label="Quotation by"
              name="Renewable Philippines"
              data={[
                constants.address,
                constants.phone,
                constants.email,
                `DTI Reg: ${constants.dti}`,
                `BIR: ${constants.bir}`,
              ]}
            />

            <QuotationResultContact
              label="Quotation to"
              name={data.name}
              data={[data.address, data.phone, data.email]}
            />
          </div>

          <QuotationResultText
            label={`Dear ${data.name},`}
            texts={[
              `Thank you for choosing Renewable Philippines as your partner in transitioning to solar energy. Based on your monthly electricity consumption of Php ${data.electricBill.toLocaleString()}, we have prepared an indicative solar proposal tailored to your energy needs.`,
              "Please note that this proposal is for informational purposes only and is designed to provide an estimate of the potential cost of your solar installation. The final cost may vary depending on your property’s specific requirements and conditions.",
            ]}
          />

          <QuotationResultText
            label="Your Estimated System Cost:"
            texts={[
              `Starting from <highlight>Php ${
                data.range
              }</highlight> for a <highlight>${data.size.toFixed(
                1
              )}kW</highlight> system`,
            ]}
          />

          <QuotationResultGrid
            data={systemBreakdown}
            label="System Breakdown:"
          />

          <QuotationResultList
            data={inclusions}
            label="Exclusive Inclusions:"
          />
        </div>
      </div>
    </Modal>
  );
};

export default QuotationResult;
